<template>
  <v-form ref="form" v-model="formValid" lazy-validation>
    <v-container fluid>
      <v-layout row wrap>
        <v-flex xs12>
          <h4>{{ 'app.surveys.add_own_questions_to_interview' | trans }}</h4>
          <v-autocomplete
              :value="selectedSurvey"
              :items="surveys"
              :label="'app.surveys.survey' | trans"
              item-text="name"
              item-value="id"
              :disabled="!hasPermission('surveys_edit')"
              return-object
              hide-details
              clearable
              v-on:change="selectedSurveyChanged(selectedSurvey, $event)"
          >
            <template v-slot:selection="data">
              {{ data.item.name }}
              <template v-if="data.item.shared">
                <v-tooltip right>
                  <template #activator="{ on }">
                    <v-icon v-on="on" small color="success">share</v-icon>
                  </template>
                  <span>{{ trans('app.sharing.shared_by', {name: data.item.owned_by}) }}</span>
                </v-tooltip>
              </template>
            </template>
            <template v-slot:item="data">
              <template>
                {{ data.item.name }}
                <template v-if="data.item.shared">
                  <v-tooltip right>
                    <template #activator="{ on }">
                      <v-icon v-on="on" small color="success">share</v-icon>
                    </template>
                    <span>{{ trans('app.sharing.shared_by', {name: data.item.owned_by}) }}</span>
                  </v-tooltip>
                </template>
              </template>
            </template>
          </v-autocomplete>
        </v-flex>
      </v-layout>
      <v-layout wrap align-center>
        <v-flex xs12>
          <h4>{{ 'app.surveys.additional_questions' | trans }}</h4>
          <draggable v-model="model.questions" class="mt-4 mb-3" handle=".question_drag_icon"
                     @end="compareQuestionsWithInitialState">
            <div class="list-group-item"
                 v-for="question in model.questions"
                 :key="question.order"
            >
              <v-layout wrap align-center>
                <v-flex xs10>
                  <v-layout align-center justify-start>
                    <v-icon class="question_drag_icon mr-2" small>swap_vert</v-icon>
                    <v-text-field
                        v-model="question.answer"
                        :label="question.text"
                        :disabled="!hasPermission('surveys_create')"
                    >
                    </v-text-field>
                  </v-layout>
                </v-flex>
                <v-flex xs2>
                  <v-layout align-center justify-end>
                    <edit-question-modal
                        :question="question.text"
                        @closed="editQuestionModalClosed(question, $event)"
                    ></edit-question-modal>
                    <icon-button
                        @click="copyQuestion(question)"
                        icon="file_copy"
                        color="light-blue"
                        cls="mr-0 ml-0"
                        :permission="hasPermission('surveys_create')"
                        :tooltip="{top: true, bottom: false, attach: false}"
                        :iconOptions="{small: true}"
                    >
                      {{ 'app.surveys.copy_question' | trans }}
                    </icon-button>
                    <icon-button
                        icon="delete"
                        color="red"
                        cls="mr-0 ml-0"
                        :permission="hasPermission('surveys_create')"
                        @click="deleteQuestion(question)"
                        :tooltip="{top: true, bottom: false, attach: false}"
                        :iconOptions="{small: true}"
                    >
                      {{ 'app.surveys.delete_question' | trans }}
                    </icon-button>
                  </v-layout>
                </v-flex>
              </v-layout>
            </div>
          </draggable>
        </v-flex>
        <v-flex xs8 md7>
          <v-form ref="questionForm" v-model="questionValid" lazy-validation
                  v-on:submit.prevent="addQuestion(question)">
            <v-text-field
                :label="'app.surveys.question' | trans"
                clearable
                v-model="question"
                :rules="rules.question"
                :disabled="!hasPermission('surveys_create')"
                full
            ></v-text-field>
          </v-form>
        </v-flex>
        <v-flex xs2 md2>
          <permission-tooltip
              :permission="hasPermission('surveys_create')"
              :tooltip="{top: true, bottom: false, attach: false}"
          >
            <v-btn color="primary" @click="addQuestion(question)"
                   :disabled="!hasPermission('surveys_create') || !questionValid">
              {{ 'app.surveys.add_question' | trans }}
            </v-btn>
          </permission-tooltip>
        </v-flex>
        <v-flex xs12 md12 lg2 class="mt-4" v-if="hasPermission('surveys_create') && questionsEdited">
          <add-survey-modal
              ref="addSurveyModal"
              :currentSurvey="null"
              :overwrite-open-modal-button-text="trans('app.surveys.save_as_new_survey')"
              :overwrite-modal-header="trans('app.surveys.save_as_new_survey')"
              :saving-as-template-mode="true"
              @survey_stored="savedQuestionAsSurvey"
          />
        </v-flex>
        <v-flex xs12>
          <v-messages class="ml-2" v-if="hasEmptyQuestions()"
                      :value="[trans('app.surveys.validation.questions.empty')]"
                      :color="`error`"></v-messages>
        </v-flex>
      </v-layout>
    </v-container>
    <v-divider v-if="showSaveButton"></v-divider>
    <v-container fluid v-if="showSaveButton">
      <v-layout align-center justify-end>
        <v-btn @click="cancel()" :disabled="loading">{{ 'app.cancel' | trans }}</v-btn>
        <v-btn color="primary" @click="save()" :disabled="!formValid || loading" :loading="loading">
          {{ 'app.save' | trans }}
        </v-btn>
      </v-layout>
    </v-container>
  </v-form>
</template>
<script>
import {trans} from "../../../app";
import draggable from 'vuedraggable'
import {mapGetters, mapActions, mapMutations} from "vuex";
import {helpers} from "../../../helpers";

export default {
  components: {
    draggable
  },
  props: {
    showSaveButton: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      formValid: false,
      loading: false,
      selectedSurvey: null,
      model: {
        questions: [],
      },
      question: null,
      questionValid: false,
      shouldValidateEmptyQuestions: false,
      questionsEdited: false,
      initialQuestionsState: null,
    }
  },
  created() {
    this.loadModel();
    this.saveInitialQuestionsState();
  },
  async mounted() {
    try {
      await this.fetchSurveys();
    } catch (error) {
      this.sentryCaptureException(error);
    }
  },
  computed: {
    ...mapGetters(['patient', 'interview', 'hasPermission', 'surveys']),
    rules: function () {
      return {
        question: [
          v => !!v || trans('app.surveys.validation.question.required'),
          v => v && v.length >= 1 || trans('app.surveys.validation.question.min', {min: '1'}),
          v => v && v.length <= 255 || trans('app.surveys.validation.question.max', {max: '255'}),
        ],
      };
    },
    emptyQuestions: function () {
      return this.model.questions.length === 0;
    }
  },
  methods: {
    ...mapActions(['updateInterview', 'fetchSurveys']),
    ...mapMutations(['setCustomQuestions']),
    loadModel() {
      if (this.interview && this.interview.questions) {
        this.model.questions = this.interview.questions;
      }
    },
    cancel() {
      this.loadModel();
      this.$emit('canceled');
    },
    async save() {
      this.loading = true;
      if (this.$refs.form.validate() === false) {
        this.loading = false;
        this.$toast.error(trans('app.save_failed'));
        return false;
      }

      let updatedInterview = helpers.deepClone(this.interview);
      updatedInterview.questions = this.model.questions;

      try {
        await this.updateInterview(updatedInterview);
      } catch (error) {
        this.$toast.error(trans('app.save_failed'));
        this.loading = false;
        this.sentryCaptureException(error);
        return false;
      }

      this.loading = false;
      this.$toast.success(trans('app.actions.saved', {name: trans('app.patients.panel.interview').toLowerCase()}));
      this.$emit('saved');
      return true;
    },
    copyQuestion(question) {
      this.selectedSurvey = null;
      this.addQuestion(question.text);
    },
    deleteQuestion(question) {
      this.$dialog.confirm(trans('app.surveys.confirm_delete_question')).then(() => {
        this.selectedSurvey = null;
        this.shouldValidateEmptyQuestions = true;
        this.model.questions = this.model.questions.filter((surveyQuestion) => {
          return surveyQuestion.order !== question.order;
        });
        this.compareQuestionsWithInitialState();
      }).catch(() => {
      });
    },
    addQuestion(question) {
      this.selectedSurvey = null;
      this.shouldValidateEmptyQuestions = true;
      if (question) {
        this.model.questions.push({
          text: question,
          order: this.model.questions.length + 1,
          answer: null,
        });
        this.question = null;
        this.$refs.questionForm.reset();
        this.compareQuestionsWithInitialState();
      }
    },
    hasEmptyQuestions() {
      if (this.shouldValidateEmptyQuestions === false) {
        return false;
      }
      return this.emptyQuestions;
    },
    selectedSurveyChanged(selectedSurvey, newSelectedSurvey) {
      if (this.selectedSurvey || this.model.questions.length > 0) {
        this.$dialog.confirm(trans('app.interview.confirm_survey_change', {"survey": newSelectedSurvey.name})).then(() => {
          this.changeSurvey(newSelectedSurvey);
        }).catch(() => {
        });
      } else {
        this.changeSurvey(newSelectedSurvey);
      }
    },
    changeSurvey(newSelectedSurvey) {
      this.selectedSurvey = newSelectedSurvey;
      this.model.questions = this.selectedSurvey.questions.map((question) => {
        return {
          text: question.text,
          order: question.order,
          answer: null,
        }
      });
    },
    setSurvey(survey) {
      if (survey) {
        this.selectedSurvey = survey;
        this.model.questions = this.selectedSurvey.questions.map((question) => {
          return {
            text: question.text,
            order: question.order,
            answer: null,
          }
        });
      }
    },
    editQuestionModalClosed(question, payload) {
      question.text = payload.question;
      this.compareQuestionsWithInitialState();
    },
    transformQuestionsToComparable() {
      return JSON.stringify(this.model.questions.map((question) => {
        return {
          text: question.text,
          order: question.order
        }
      }));
    },
    saveInitialQuestionsState() {
      this.initialQuestionsState = this.transformQuestionsToComparable();
    },
    compareQuestionsWithInitialState() {
      const comparable = this.transformQuestionsToComparable()
      this.setCustomQuestions(JSON.parse(comparable));
      this.questionsEdited = this.initialQuestionsState !== comparable;
    },
    async savedQuestionAsSurvey() {
      this.saveInitialQuestionsState();
      this.compareQuestionsWithInitialState();
      await this.fetchSurveys()
    }
  }
}
</script>
<style scoped>
.question_drag_icon:hover {
  cursor: grab;
}
</style>